import { ConfirmButton } from "@/v2-backoffice-client/components/button"
import RelativeDate from "@/v2-backoffice-client/components/RelativeDate"
import TenantPrivacyHistoryButton from "@/v2-backoffice-client/tenant/privacy/TenantPrivacyHistoryButton"
import TenantPrivacyPresetBadge from "@/v2-backoffice-client/tenant/privacy/TenantPrivacyPresetBadge"
import type { PrivacySummary } from "@/v2-backoffice-client/tenant/privacy/tenant.privacy.types"

export type TenantPrivacyPolicyHeaderProps = {
  tenantId: number
  summary: PrivacySummary
  /** How many private-data sources exist in total, for the "n of m" read. */
  sourceCount: number
  isBusy: boolean
  isResetting: boolean
  onReset(): Promise<void>
}

// Where the team stands today, and the two actions that concern the policy
// as a whole: its history and its reset. "Matches" is computed server-side
// against the EFFECTIVE policy, so a team whose custom rows happen to equal
// a preset shows as being on it — which is the honest answer. The pill is
// the same component the teams list and the team header render, fed from
// the same cache record, so the three never disagree.
function TenantPrivacyPolicyHeader(props: TenantPrivacyPolicyHeaderProps) {
  const { tenantId, summary, sourceCount, isBusy, isResetting, onReset } = props

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm font-semibold">Current policy</span>
      <TenantPrivacyPresetBadge summary={summary} />
      <span className="text-xs text-muted-foreground">
        · {summary.PolicyRowCount} of {sourceCount} sources configured
        {summary.LastUpdatedAt && (
          <>
            {" · last change "}
            <RelativeDate date={summary.LastUpdatedAt} />
          </>
        )}
      </span>

      <div className="flex-1" />

      <TenantPrivacyHistoryButton tenantId={tenantId} disabled={isBusy} />

      {summary.PolicyRowCount > 0 && (
        <ConfirmButton
          type="button"
          size="xs"
          variant="ghost"
          confirmVariant="destructive"
          confirmIconSrc="trash"
          confirmContent="Reset every source to hidden? The change history keeps what was configured."
          isLoading={isResetting}
          disabled={isBusy}
          data-feature-code="UI_BackofficeTenantPrivacy_Policy_Reset"
          onConfirm={onReset}
        >
          Reset to Hidden
        </ConfirmButton>
      )}
    </div>
  )
}

export default TenantPrivacyPolicyHeader
